import React from 'react';
import type { SlotOption } from '../../../models/entitlement/SlotOption';
import { getClassAvailability, cheapestPaidSeatsCents } from '../../../utils/seatAvailability';
import { formatUsTime } from '../../../utils/bookingUtils';
import './SlotRows.css';

interface Props {
  slots: SlotOption[];
  /** Chosen slot id, or '' when nothing is picked. */
  value: string;
  onChange: (slotId: string) => void;
  /** Everyone racing, including the member — drives the rig badges. */
  people: number;
  /** Seats that cost money: the guests. The member's own seat is $0 on their entitlement. */
  paidSeats: number;
  /** The member's simulator — it occupies a rig, so the guests cannot also be quoted for it. */
  memberRig?: 'pro' | 'esports';
  /**
   * The member tier's guest discount, in basis points. Without it the row quoted list price while
   * the summary and the charge were discounted — $115.00 here beside $80.50 below for a VIP.
   */
  guestDiscountBp?: number;
  isLoading?: boolean;
  labelledBy?: string;
}

const money = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

const RIG_LABEL = { pro: 'Pro', esports: 'eSports' } as const;

/**
 * One row per start time, each showing what the two simulator classes can seat and what the paid
 * seats would cost there.
 */
const SlotRows: React.FC<Props> = ({
  slots, value, onChange, people, paidSeats, memberRig, guestDiscountBp = 0, isLoading = false,
  labelledBy,
}) => {
  if (isLoading) return <p className="slot-empty">Loading available times…</p>;
  if (slots.length === 0) {
    return <p className="slot-empty">No times on this day for a group this size — try another day.</p>;
  }

  return (
    <div className="slot-list" role="group" aria-labelledby={labelledBy}>
      {slots.map((slot) => {
        const pro = getClassAvailability(slot, people, 'pro');
        const esports = getClassAvailability(slot, people, 'esports');
        // Blocked ONLY when neither class has anything. A party splits across classes, so a slot
        // with 1 Pro and 2 eSports still seats three people.
        const soldOut = pro.status === 'sold-out' && esports.status === 'sold-out';
        const fromCents = soldOut ? undefined : cheapestPaidSeatsCents(slot, paidSeats, memberRig, guestDiscountBp);
        const showPrice = fromCents !== undefined && paidSeats > 0;

        const tag = (
          rig: 'pro' | 'esports',
          a: { status: 'available' | 'limited' | 'sold-out'; count: number },
        ) => (
          <span className={`rig-tag ${a.status}`}>
            {RIG_LABEL[rig]}{' '}
            {a.status === 'available' ? '✓' : a.status === 'limited' ? `${a.count} avail` : 'sold out'}
          </span>
        );

        return (
          <button
            key={slot.slotId}
            type="button"
            className="slot-row"
            aria-pressed={value === slot.slotId}
            disabled={soldOut}
            onClick={() => onChange(slot.slotId)}
          >
            <span className="slot-row-left">
              <span className="slot-time">{formatUsTime(slot.startTime)}</span>
              <span className="slot-rigs">
                {tag('pro', pro)}
                {tag('esports', esports)}
              </span>
            </span>

            {/* Covers EVERY paid seat, not one of them, and rises when the cheaper class runs
                out — so the figure tracks what this slot would actually cost.

                ALWAYS rendered, even with no guests: the price is the tallest thing in the row, so
                dropping the element made every row shrink and the whole list jump as the party
                stepper moved. `visibility: hidden` keeps the space without showing a price the
                member is not paying. */}
            <span className={`slot-from${showPrice ? '' : ' slot-from-empty'}`} aria-hidden={!showPrice}>
              <span className="slot-from-label">
                {paidSeats || 1} guest{(paidSeats || 1) === 1 ? '' : 's'} from
              </span>
              <span className="slot-from-amount">{showPrice ? money(fromCents as number) : '$0.00'}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default SlotRows;
